import { inject, Injectable, Injector } from '@angular/core';
import { MessageBase, MessageModel, ToastrConfig } from '@ebeihilfe/core';
import { TranslateService } from '@ngx-translate/core';
import { IndividualConfig, ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';

@Injectable()
export class MessageService extends MessageBase {
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);

  private readonly toastrOptions: Partial<IndividualConfig<ToastrConfig>> = {
    timeOut: MessageBase.TOAST_DURATION,
    positionClass: 'toast-top-right',
    progressBar: true,
    closeButton: true,
    enableHtml: false,
    tapToDismiss: true,
    onActivateTick: true,
    newestOnTop: true,
    disableTimeOut: false,
  };

  /**
   * Need to get ToastrService from injector rather than constructor injection to avoid cyclic dependency error
   */
  private get toastr(): ToastrService {
    return this.injector.get(ToastrService);
  }

  public async showError(error: MessageModel | string): Promise<void> {
    if (typeof error === 'string') {
      await this.showError({ message: error });
      return;
    }

    let message = error.message && typeof error.message === 'string' ? error.message : '';
    // todo all this should be in error handling not message sending
    const isUncaught = message.toLowerCase().includes('uncaught');
    if (!message || isUncaught) {
      message = 'general.label.error';
    }

    // wait for i18n to be loaded (if not already)
    if (this.translateService.langs.length === 0) {
      await firstValueFrom(this.translateService.onLangChange);
    }

    this.toastr.error(this.translateService.instant(message, error.messageParam), '', this.toastrOptions);
  }

  public showInfo(info: MessageModel): Promise<void> {
    this.toastr.info(this.translateService.instant(info.message, info.messageParam), '', this.toastrOptions);
    return Promise.resolve();
  }

  public showSuccess(success: MessageModel): Promise<void> {
    this.toastr.success(this.translateService.instant(success.message, success.messageParam), '', this.toastrOptions);
    return Promise.resolve();
  }
}
